import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './auth';
import { fetchJSON } from './api';
// restoreCompletions is defined later in this file; imported inline by the hook.

// Per-course self-check completion tracking. The engagement gate that locks
// the final quiz reads from here.
//
// Architecture (two-layer):
//   SERVER (authoritative): quiz_attempts rows, written by POST /api/quizzes/attempts
//     whenever req.user is present. The GET /api/quizzes/course/:slug/self-check-progress
//     endpoint returns { completed, total } from a COUNT(DISTINCT) query.
//     This is the source of truth for authenticated users — accurate across
//     devices and after sign-out/re-login.
//
//   LOCAL (cache + unauthenticated fallback): localStorage under
//     lo:self_check_progress:<slug>. Serves two purposes:
//       1. Per-question locked-state restoration (correct_index + explanation
//          cached so a page reload restores the answered card without another
//          server hit — safe because the server already told us the correct
//          answer at submit time).
//       2. Unauthenticated / review-mode progress (review mode uses
//          markAllSelfChecksComplete placeholder entries instead).
//
// The hook:
//   - When authenticated, fetches the server count on mount and after each
//     local write event (lo:self_check_progress). Server count drives
//     `count` and `allComplete`. localStorage drives per-question state.
//   - When unauthenticated, falls back to localStorage count only.

const KEY_PREFIX = 'lo:self_check_progress:';

export const SELF_CHECK_TOTAL = {
  'ai-for-parents': 48,
};

function storageKey(courseSlug) {
  return `${KEY_PREFIX}${courseSlug}`;
}

function isUsableEntry(e) {
  if (!e || !Number.isFinite(e.question_id)) return false;
  if (e.question_id < 0) return true;
  if (Number.isFinite(e.correct_index)) return true;
  return false;
}

function safeParse(raw) {
  if (!raw) return { completed: [] };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.completed)) {
      return { completed: parsed.completed.filter(isUsableEntry) };
    }
    if (parsed && Array.isArray(parsed.completed_question_ids)) {
      return { completed: [] };
    }
  } catch (_) {}
  return { completed: [] };
}

function readState(courseSlug) {
  if (typeof window === 'undefined') return { completed: [] };
  try {
    return safeParse(window.localStorage.getItem(storageKey(courseSlug)));
  } catch (_) {
    return { completed: [] };
  }
}

function writeState(courseSlug, state) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(courseSlug), JSON.stringify(state));
    window.dispatchEvent(new CustomEvent('lo:self_check_progress', {
      detail: { courseSlug },
    }));
  } catch (_) {}
}

export function getCompletedIds(courseSlug) {
  return readState(courseSlug).completed.map((e) => e.question_id);
}

export function getCompletionCount(courseSlug) {
  return readState(courseSlug).completed.length;
}

export function getCompletion(courseSlug, questionId) {
  const id = Number(questionId);
  if (!Number.isFinite(id)) return null;
  const e = readState(courseSlug).completed.find((c) => c.question_id === id);
  return e || null;
}

export function isComplete(courseSlug, questionId) {
  return getCompletion(courseSlug, questionId) != null;
}

export function markSelfCheckComplete(
  courseSlug,
  questionId,
  { correctIndex = null, explanation = null } = {}
) {
  const id = Number(questionId);
  if (!Number.isFinite(id)) return;
  const state = readState(courseSlug);
  const existing = state.completed.find((c) => c.question_id === id);
  if (existing) {
    let mutated = false;
    if (Number.isFinite(correctIndex) && existing.correct_index !== correctIndex) {
      existing.correct_index = correctIndex;
      mutated = true;
    }
    if (explanation && existing.explanation !== explanation) {
      existing.explanation = explanation;
      mutated = true;
    }
    if (mutated) writeState(courseSlug, state);
    return;
  }
  state.completed = [
    ...state.completed,
    {
      question_id: id,
      correct_index: Number.isFinite(correctIndex) ? correctIndex : null,
      explanation: explanation || null,
    },
  ];
  writeState(courseSlug, state);
}

// Review-mode helper — fills the engagement gate with placeholder completions
// in a NEGATIVE id namespace so they can never collide with real question ids.
export function markAllSelfChecksComplete(courseSlug) {
  const total = SELF_CHECK_TOTAL[courseSlug] || 0;
  if (!total) return 0;
  const state = readState(courseSlug);
  const existing = new Set(state.completed.map((c) => c.question_id));
  const additions = [];
  for (let i = 1; i <= total; i++) {
    const placeholderId = -i;
    if (!existing.has(placeholderId)) {
      additions.push({ question_id: placeholderId, correct_index: null, explanation: null });
    }
  }
  if (additions.length === 0) return 0;
  state.completed = [...state.completed, ...additions];
  writeState(courseSlug, state);
  return additions.length;
}

// Bulk-restore completions from server data without firing multiple events.
// Called once after a server fetch on login so every previously-answered
// QuizCheck card renders in the locked-correct state without another round-trip.
export function restoreCompletions(courseSlug, completions) {
  if (!Array.isArray(completions) || completions.length === 0) return;
  const state = readState(courseSlug);
  const existingIds = new Set(state.completed.map((c) => c.question_id));
  let changed = false;
  for (const c of completions) {
    const id = Number(c.question_id);
    if (!Number.isFinite(id) || id <= 0) continue;
    if (existingIds.has(id)) continue;
    state.completed.push({
      question_id: id,
      correct_index: Number.isFinite(c.correct_index) ? c.correct_index : null,
      explanation: c.explanation || null,
    });
    existingIds.add(id);
    changed = true;
  }
  if (!changed) return;
  // Write once and fire a single event rather than one per completion.
  try {
    window.localStorage.setItem(storageKey(courseSlug), JSON.stringify(state));
    window.dispatchEvent(new CustomEvent('lo:self_check_progress', {
      detail: { courseSlug },
    }));
  } catch (_) {}
}

export function clearSelfCheckProgress(courseSlug) {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(storageKey(courseSlug)); } catch (_) {}
  try {
    window.dispatchEvent(new CustomEvent('lo:self_check_progress', {
      detail: { courseSlug },
    }));
  } catch (_) {}
}

// React hook — returns { completedIds, count, total, allComplete }.
//
// When the user is authenticated:
//   - Fetches the server count on mount (and re-fetches after local writes).
//   - `count` comes from the DB — accurate across all devices.
//   - `completedIds` still comes from localStorage (per-question locked UI).
//
// When unauthenticated (review mode / not logged in):
//   - Falls back to localStorage count only.
export function useSelfCheckProgress(courseSlug) {
  const { user } = useAuth();
  const [localTick, setLocalTick] = useState(0);
  const [serverCount, setServerCount] = useState(null);
  const fetchingRef = useRef(false);

  const bumpLocal = useCallback(() => setLocalTick((t) => t + 1), []);

  // Listen for local writes (same-tab custom event + cross-tab storage event).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onStorage = (e) => {
      if (!e.key || e.key.startsWith(KEY_PREFIX)) bumpLocal();
    };
    const onCustom = (e) => {
      if (!e.detail || !e.detail.courseSlug || e.detail.courseSlug === courseSlug) {
        bumpLocal();
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('lo:self_check_progress', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('lo:self_check_progress', onCustom);
    };
  }, [courseSlug, bumpLocal]);

  // Fetch from server whenever user is authenticated and localTick changes
  // (i.e. immediately on mount, and again after each local completion write).
  // Also restores per-question locked-card state in localStorage so QuizCheck
  // cards render "Correct!" correctly after a sign-out + re-login cycle.
  useEffect(() => {
    if (!user || !courseSlug) {
      setServerCount(null);
      return;
    }
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    fetchJSON(`/api/quizzes/course/${courseSlug}/self-check-progress`)
      .then((data) => {
        if (!data) return;
        if (Number.isFinite(data.completed)) {
          setServerCount(data.completed);
        }
        // Restore per-question locked state so cards that were previously
        // answered show as "Correct!" even when localStorage was cleared.
        if (Array.isArray(data.completions) && data.completions.length > 0) {
          restoreCompletions(courseSlug, data.completions);
        }
      })
      .catch(() => {})
      .finally(() => { fetchingRef.current = false; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, courseSlug, localTick]);

  void localTick;

  const completedIds = getCompletedIds(courseSlug);
  const localCount = completedIds.length;

  // Server count is authoritative when authenticated. Fall back to local count
  // for unauthenticated users and while the server fetch is still in flight.
  const count = (user && serverCount !== null) ? serverCount : localCount;
  const total = SELF_CHECK_TOTAL[courseSlug] || 0;
  const allComplete = total > 0 && count >= total;

  return { completedIds, count, total, allComplete };
}
